import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _defineProperty from '@babel/runtime/helpers/defineProperty';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import _extends from '@babel/runtime/helpers/extends';
import * as React from 'react';
import { Icon, useThemeContext, Box, Typography, useUniqueId, SkeletonLoader } from '@drone-js/core';
import { format } from 'date-fns';
import { useDayPicker, labelNext, labelPrevious } from 'react-day-picker';
import { CalendarHeader } from './CalendarHeader.js';
import { CalendarLabel } from './CalendarLabel.js';
import { getSpacingToken, getDateTypographyColor, getDateBackgroundColor, getDateBorderRadius, getDateWrapperBackgroundColor, getTodayBackgroundColor, getDatePriceLabelColor, getEndDate } from './DatePicker.mappers.js';
import { dayPickerStyles } from './DatePickerStyles.js';
import { formatMonth, formatYear } from './util/index.js';

var _excluded = ["day", "modifiers", "hasPriceLabel", "priceLabelMap", "isLoading", "isScrollable", "loadingLabel", "children", "getDateAriaLabel"],
  _excluded2 = ["children", "isScrollable"],
  _excluded3 = ["isScrollable"],
  _excluded4 = ["week"],
  _excluded5 = ["calendarMonth", "displayIndex", "children", "isScrollable"],
  _excluded6 = ["isScrollable"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var getLabelMapKey = function getLabelMapKey(date) {
  return "".concat(date.getFullYear(), "-").concat(date.getMonth(), "-").concat(date.getDate());
};
var getDatePriceTrendIcon = function getDatePriceTrendIcon(colorVariant, labelColor, ariaDescription) {
  if (colorVariant === 'neutral') return null;
  var labelProp = ariaDescription ? {
    label: ariaDescription
  } : {};
  return colorVariant === 'priceHigh' ? /*#__PURE__*/React.createElement(Icon, _extends({
    id: "fill.arrow.thick-collapse",
    size: "12",
    color: labelColor
  }, labelProp)) : /*#__PURE__*/React.createElement(Icon, _extends({
    id: "fill.arrow.thick-dropdown",
    size: "12",
    color: labelColor
  }, labelProp));
};
var DayButton = function DayButton(props) {
  var dir = props.dir,
    modifiers = props.modifiers,
    day = props.day,
    enabledToday = props.today;
  var date = day.date;
  var theme = useThemeContext();
  var selected = modifiers.selected,
    disabled = modifiers.disabled,
    isToday = modifiers.today,
    rangeStart = modifiers.range_start,
    rangeMiddle = modifiers.range_middle,
    rangeEnd = modifiers.range_end;
  var today = enabledToday && isToday;
  var spacing = getSpacingToken(theme);
  var typographyColor = getDateTypographyColor(disabled, selected, rangeMiddle);
  var backgroundColor = getDateBackgroundColor(selected, rangeMiddle);
  var borderRadius = getDateBorderRadius(dir, selected, rangeStart, rangeEnd);
  var wrapperBackgroundColor = getDateWrapperBackgroundColor(rangeStart, rangeMiddle, rangeEnd);
  var todayBackgroundColor = getTodayBackgroundColor(selected, rangeMiddle);
  var dateInnerBoxStyles = {
    width: '36px',
    height: '36px',
    interactive: disabled ? undefined : 'product.primary',
    borderRadius: borderRadius
  };
  var todayComponent = /*#__PURE__*/React.createElement(Box, {
    position: "absolute",
    left: "auto",
    right: "auto",
    bottom: spacing['4'],
    width: spacing['12'],
    height: spacing['2'],
    borderRadius: "2",
    backgroundColor: todayBackgroundColor
  });
  var dateTypography = /*#__PURE__*/React.createElement(Typography, {
    variant: "body-default",
    textColor: typographyColor,
    interactive: selected && !rangeMiddle ? 'none' : undefined
  }, format(date, 'd'));
  var dateComponent = /*#__PURE__*/React.createElement(Box, _extends({
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    alignSelf: "center",
    p: "4",
    mx: "auto",
    position: "relative"
  }, dateInnerBoxStyles), dateTypography, today && todayComponent);
  return /*#__PURE__*/React.createElement(Box, {
    display: "flex",
    justifyContent: "center",
    width: "100%",
    height: "36px",
    backgroundColor: wrapperBackgroundColor
  }, /*#__PURE__*/React.createElement(Box, _extends({}, dateInnerBoxStyles, {
    backgroundColor: backgroundColor,
    interactiveChild: true
  }), dateComponent));
};
var DayCell = function DayCell(props) {
  var day = props.day,
    modifiers = props.modifiers,
    hasPriceLabel = props.hasPriceLabel,
    priceLabelMap = props.priceLabelMap,
    isLoading = props.isLoading,
    isScrollable = props.isScrollable,
    loadingLabel = props.loadingLabel,
    children = props.children,
    getDateAriaLabel = props.getDateAriaLabel,
    rest = _objectWithoutProperties(props, _excluded);
  var date = day.date;
  var selected = modifiers.selected,
    today = modifiers.today,
    disabled = modifiers.disabled,
    hidden = modifiers.hidden,
    outside = modifiers.outside,
    focusedDay = modifiers.focused,
    customTabbable = modifiers.customTabbable;
  var childProps = /*#__PURE__*/React.isValidElement(children) ? children.props : undefined;
  var onBlur = childProps.onBlur,
    onClick = childProps.onClick,
    onFocus = childProps.onFocus,
    onKeyDown = childProps.onKeyDown;
  var describedById = useUniqueId();
  var dateAriaLabel = getDateAriaLabel === null || getDateAriaLabel === void 0 ? void 0 : getDateAriaLabel(date);
  var buttonRef = React.useRef(null);
  React.useEffect(function () {
    var _buttonRef$current;
    if (modifiers.focused) (_buttonRef$current = buttonRef.current) === null || _buttonRef$current === void 0 || _buttonRef$current.focus();
  }, [modifiers.focused]);
  var modifiedButtonProps = _defineProperty({
    className: undefined,
    role: undefined,
    'aria-label': dateAriaLabel,
    'aria-current': today && 'date',
    'aria-describedby': describedById
  }, 'aria-selected', undefined);
  var baseStyle = isScrollable ? dayPickerStyles.mobileDayCell : dayPickerStyles.dayCell;
  var labelStyle = isScrollable ? dayPickerStyles.mobileDayCellWithLabel : dayPickerStyles.dayCellWithLabel;
  var cellStyle = hasPriceLabel ? _objectSpread(_objectSpread({}, baseStyle), labelStyle) : baseStyle;

  // focus and hover state logic is to ensure the date number remains white when selected && hovered or focused
  var _React$useState = React.useState(false),
    _React$useState2 = _slicedToArray(_React$useState, 2),
    hovered = _React$useState2[0],
    setHovered = _React$useState2[1];
  var _React$useState3 = React.useState(false),
    _React$useState4 = _slicedToArray(_React$useState3, 2),
    focused = _React$useState4[0],
    setFocused = _React$useState4[1];
  React.useEffect(function () {
    if (focusedDay) {
      setFocused(true);
    }
    if (!focusedDay) {
      setFocused(false);
    }
  }, [focusedDay, date, focused]);
  if (hidden || outside) {
    var emptyLabel = 'empty grid cell';
    return /*#__PURE__*/React.createElement("td", _extends({}, rest, {
      style: cellStyle
    }), /*#__PURE__*/React.createElement("div", {
      role: "gridcell",
      "aria-label": emptyLabel
    }));
  }
  var zIndex = focused && selected ? 1 : undefined;
  var ariaSelected = selected ? 'true' : 'false';
  var key = getLabelMapKey(date);
  var priceLabel = priceLabelMap === null || priceLabelMap === void 0 ? void 0 : priceLabelMap[key];
  var colorVariant = (priceLabel === null || priceLabel === void 0 ? void 0 : priceLabel.colorVariant) || 'neutral';
  var labelColor = getDatePriceLabelColor(colorVariant, hovered, focused, disabled);
  var priceTrendIcon = getDatePriceTrendIcon(colorVariant, labelColor, priceLabel === null || priceLabel === void 0 ? void 0 : priceLabel.ariaDescription);
  return /*#__PURE__*/React.createElement("td", _extends({}, rest, {
    style: cellStyle,
    "aria-selected": ariaSelected
  }), /*#__PURE__*/React.createElement(Box, {
    width: "100%",
    height: "100%",
    minHeight: "inherit",
    onMouseEnter: function onMouseEnter() {
      return setHovered(true);
    },
    onMouseLeave: function onMouseLeave() {
      return setHovered(false);
    }
  }, /*#__PURE__*/React.createElement(Box, _extends({
    p: "0",
    width: "100%",
    height: "100%",
    zIndex: zIndex,
    minHeight: "inherit",
    interactiveWithin: "product.primary",
    outlineStyle: "none",
    as: "button",
    ref: buttonRef,
    "data-testid": format(date, 'PPPP'),
    id: disabled ? undefined : 'active-day',
    tabIndex: customTabbable && !disabled ? 0 : -1,
    onClick: onClick,
    onBlur: onBlur,
    onKeyDown: onKeyDown,
    onFocus: onFocus,
    cursor: disabled ? 'default' : 'pointer',
    disabled: disabled
  }, modifiedButtonProps), children), hasPriceLabel && /*#__PURE__*/React.createElement(Box, {
    id: describedById,
    width: "100%",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    flexDirection: "column",
    mt: "2"
  }, isLoading ? /*#__PURE__*/React.createElement(SkeletonLoader, {
    width: "24px",
    height: "8px",
    "data-testid": "date-picker-label-loader",
    label: loadingLabel
  }) : /*#__PURE__*/React.createElement(Box, {
    display: "flex",
    justifyContent: "center",
    minHeight: "13px"
  }, priceTrendIcon, /*#__PURE__*/React.createElement(Typography, {
    variant: "caption-default",
    textColor: labelColor
  }, priceLabel === null || priceLabel === void 0 ? void 0 : priceLabel.label)))));
};
var MonthLabel = function MonthLabel(props) {
  var _props$numberOfCalend = props.numberOfCalendar,
    numberOfCalendar = _props$numberOfCalend === void 0 ? 1 : _props$numberOfCalend,
    calendarMonth = props.calendarMonth,
    locale = props.locale,
    monthYearPicker = props.monthYearPicker,
    onMonthChange = props.onMonthChange,
    onCalendarChange = props.onCalendarChange,
    isScrollable = props.isScrollable,
    headingTag = props.headingTag;
  return /*#__PURE__*/React.createElement(Box, {
    "aria-live": "polite",
    role: "presentation",
    display: "flex",
    width: "100%",
    justifyContent: "center"
  }, !monthYearPicker || isScrollable ? /*#__PURE__*/React.createElement(Box, {
    display: "flex",
    gap: "8"
  }, /*#__PURE__*/React.createElement(CalendarLabel, {
    headingTag: headingTag,
    label: formatMonth(calendarMonth.date, locale)
  }), /*#__PURE__*/React.createElement(CalendarLabel, {
    headingTag: headingTag,
    label: formatYear(calendarMonth.date, locale)
  })) :
  /*#__PURE__*/
  /* TODO: ADSREQ-552 fix Year/Month position by locale  */
  React.createElement(Box, {
    display: "flex",
    gap: "16"
  }, /*#__PURE__*/React.createElement(CalendarLabel, {
    headingTag: headingTag,
    label: formatMonth(calendarMonth.date, locale),
    onClick: function onClick() {
      onMonthChange === null || onMonthChange === void 0 || onMonthChange(calendarMonth.date);
      onCalendarChange('month', numberOfCalendar);
    }
  }), /*#__PURE__*/React.createElement(CalendarLabel, {
    headingTag: headingTag,
    label: formatYear(calendarMonth.date, locale),
    onClick: function onClick() {
      onMonthChange === null || onMonthChange === void 0 || onMonthChange(calendarMonth.date);
      onCalendarChange('year', numberOfCalendar);
    }
  })));
};
var Caption = function Caption(props) {
  var numberOfMonths = props.numberOfMonths,
    displayIndex = props.displayIndex,
    locale = props.locale,
    calendarMonth = props.calendarMonth,
    startDate = props.startDate,
    endDate = props.endDate;
  var _useDayPicker = useDayPicker(),
    previousMonth = _useDayPicker.previousMonth,
    nextMonth = _useDayPicker.nextMonth,
    goToMonth = _useDayPicker.goToMonth;
  var nextLabel = labelNext(nextMonth, {
    locale: locale
  });
  var previousLabel = labelPrevious(previousMonth, {
    locale: locale
  });
  var isFirst = displayIndex === 0;
  var isLast = displayIndex === numberOfMonths - 1;
  var end = getEndDate(numberOfMonths, startDate, endDate);
  var isLastMonthWithEndDate = end && calendarMonth.date.getMonth() === end.getMonth() && calendarMonth.date.getFullYear() === end.getFullYear();
  var isNextMonthDisable = !nextMonth || isLastMonthWithEndDate;
  var hideNext = numberOfMonths > 1 && (isFirst || !isLast);
  var hidePrevious = numberOfMonths > 1 && (isLast || !isFirst);
  var handlePreviousClick = function handlePreviousClick() {
    if (!previousMonth) return;
    goToMonth(previousMonth);
  };
  var handleNextClick = function handleNextClick() {
    if (!nextMonth) return;
    goToMonth(nextMonth);
  };
  return /*#__PURE__*/React.createElement(CalendarHeader, {
    prevButton: !hidePrevious ? {
      icon: 'fill.arrow.thin-back',
      'aria-label': previousLabel,
      disabled: !previousMonth,
      onClick: handlePreviousClick
    } : undefined,
    nextButton: !hideNext ? {
      icon: 'fill.arrow.thin-forward',
      'aria-label': nextLabel,
      disabled: isNextMonthDisable,
      onClick: handleNextClick
    } : undefined,
    onKeyDown: function onKeyDown(event) {
      var activeElement = document.activeElement;
      var closestDatePickerContainer = activeElement.closest('.rdp-root');
      var closestMonthContainer = closestDatePickerContainer === null || closestDatePickerContainer === void 0 ? void 0 : closestDatePickerContainer.querySelector('tbody');
      var closestPrevNavButton = closestDatePickerContainer === null || closestDatePickerContainer === void 0 ? void 0 : closestDatePickerContainer.querySelector('#previous-month-nav');
      var closestNextNavButton = closestDatePickerContainer === null || closestDatePickerContainer === void 0 ? void 0 : closestDatePickerContainer.querySelector('#next-month-nav');
      var closestActiveDay = closestMonthContainer === null || closestMonthContainer === void 0 ? void 0 : closestMonthContainer.querySelector('button[tabIndex="0"]');
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        if (!previousMonth) return;
        closestPrevNavButton === null || closestPrevNavButton === void 0 || closestPrevNavButton.focus();
      }
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        if (!nextMonth) return;
        closestNextNavButton === null || closestNextNavButton === void 0 || closestNextNavButton.focus();
      }
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        closestActiveDay === null || closestActiveDay === void 0 || closestActiveDay.focus();
      }
    }
  }, props.children);
};
var MobileCaption = function MobileCaption(props) {
  return /*#__PURE__*/React.createElement(Box, {
    p: "8",
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between"
  }, props.children);
};
var WeekDayHeader = function WeekDayHeader(props) {
  var children = props.children,
    isScrollable = props.isScrollable,
    rest = _objectWithoutProperties(props, _excluded2);
  return /*#__PURE__*/React.createElement("th", _extends({}, rest, {
    scope: "col",
    style: isScrollable ? undefined : dayPickerStyles.daysOfTheWeekCell
  }), /*#__PURE__*/React.createElement(Box, {
    alignItems: "center",
    backgroundColor: "base.background",
    display: "flex",
    height: "40px",
    justifyContent: "center",
    p: "8",
    width: "100%"
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "body-default-strong"
  }, children)));
};
var WeekBody = function WeekBody(props) {
  return /*#__PURE__*/React.createElement("tbody", _extends({}, props, {
    style: dayPickerStyles.monthTableBody
  }));
};
var WeekDaysRow = function WeekDaysRow(props) {
  var isScrollable = props.isScrollable,
    rest = _objectWithoutProperties(props, _excluded3);
  return /*#__PURE__*/React.createElement("thead", null, /*#__PURE__*/React.createElement("tr", _extends({}, rest, {
    style: isScrollable ? dayPickerStyles.mobileDaysOfTheWeek : dayPickerStyles.daysOfTheWeek
  })));
};
var WeekRow = function WeekRow(props) {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  var week = props.week,
    rest = _objectWithoutProperties(props, _excluded4);
  return /*#__PURE__*/React.createElement("tr", _extends({}, rest, {
    style: dayPickerStyles.daysRow
  }));
};
var Month = function Month(props) {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  var calendarMonth = props.calendarMonth,
    displayIndex = props.displayIndex,
    children = props.children,
    isScrollable = props.isScrollable,
    rest = _objectWithoutProperties(props, _excluded5);
  return /*#__PURE__*/React.createElement("div", _extends({}, rest, {
    style: isScrollable ? dayPickerStyles.mobileMonth : dayPickerStyles.month
  }), children);
};
var Months = function Months(props) {
  var isScrollable = props.isScrollable,
    rest = _objectWithoutProperties(props, _excluded6);
  return /*#__PURE__*/React.createElement("div", _extends({}, rest, {
    style: isScrollable ? dayPickerStyles.mobileMonthsContainer : dayPickerStyles.monthsContainer
  }));
};
var MonthGrid = function MonthGrid(props) {
  return /*#__PURE__*/React.createElement("table", _extends({}, props, {
    style: dayPickerStyles.monthTable
  }));
};
var RootContainer = function RootContainer(props) {
  return /*#__PURE__*/React.createElement("div", _extends({}, props, {
    ref: props.ref,
    style: dayPickerStyles.root
  }));
};

export { Caption, DayButton, DayCell, MobileCaption, Month, MonthGrid, MonthLabel, Months, RootContainer, WeekBody, WeekDayHeader, WeekDaysRow, WeekRow, getLabelMapKey };
//# sourceMappingURL=DatePicker.styled.js.map
